import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, brl, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageHead, Field, inputCls, num } from "@/components/erp/ErpUi";
import { toast } from "sonner";
import { Calculator, Plus, Trash2, Wrench, Package, Lightbulb } from "lucide-react";

const card = "rounded-2xl border border-card2-border bg-card2 p-4 sm:p-5 space-y-4";
const TIER = {
  popular: { k: 1.0, lbl: "Popular", help: "até ~R$ 50 mil" },
  medio: { k: 1.25, lbl: "Médio", help: "~R$ 50 a 120 mil" },
  premium: { k: 1.6, lbl: "Premium", help: "acima de R$ 120 mil" },
};

const selectCls =
  "h-10 w-full rounded-md border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 px-3 text-zinc-900 dark:text-zinc-100";

function parseHM(s) {
  if (!s) return null;
  const [h, m] = String(s).split(":").map((n) => parseInt(n, 10));
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h + m / 60;
}
function diffHours(a, b) {
  const ha = parseHM(a), hb = parseHM(b);
  if (ha == null || hb == null) return 0;
  return Math.max(0, hb - ha);
}

function LaborCard() {
  const { refresh, user } = useAuth();
  const [f, setF] = useState({
    fixed: "",
    mechs: "2",
    wStart: "08:00", wEnd: "18:00",
    sStart: "08:00", sEnd: "12:00",
    wLunch: "1", sLunch: "0",
    hoursOverride: "",
    margin: "30",
    tier: "popular",
  });
  const [fixedHint, setFixedHint] = useState(false);
  const [out, setOut] = useState(null);

  useEffect(() => {
    api.get("/erp/finance/overview").then(({ data }) => {
      const m = Number(data?.month_fixed_costs || data?.fixed_month || 0);
      if (m > 0) setF((p) => (p.fixed === "" ? { ...p, fixed: m } : p));
      else setFixedHint(true);
    }).catch(() => setFixedHint(true));
    api.get("/employees").then(({ data }) => {
      const list = Array.isArray(data) ? data : data.items || [];
      const mech = list.filter((e) => e.active !== false && (e.role || "").toLowerCase().includes("mecan")).length;
      if (mech > 0) setF((p) => ({ ...p, mechs: String(mech) }));
    }).catch(() => {});
  }, []);

  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const wDay = Math.max(0, diffHours(f.wStart, f.wEnd) - num(f.wLunch));
  const sDay = Math.max(0, diffHours(f.sStart, f.sEnd) - num(f.sLunch));
  const perWeek = wDay * 5 + sDay;
  const fmtH = (h) => `${Math.round(h * 100) / 100}`.replace(".", ",");
  // 52 weeks / 12 months ≈ 4.3333
  const computedHoursPerMech = Math.round(perWeek * 52 / 12);
  const hoursMonth = num(f.hoursOverride) > 0
    ? num(f.hoursOverride) * num(f.mechs)
    : computedHoursPerMech * num(f.mechs);

  const run = () => {
    if (!num(f.fixed) || !hoursMonth) {
      toast.error("Preencha custos fixos e horário de trabalho.");
      setOut(null); return;
    }
    const cost = num(f.fixed) / hoursMonth;
    const base = cost * (1 + num(f.margin) / 100);
    const rate = Math.round(base * TIER[f.tier].k * 100) / 100;
    setOut({ hoursMonth, cost, rate });
  };
  const clear = () => {
    setF((p) => ({ ...p, hoursOverride: "", margin: "30", tier: "popular" }));
    setOut(null);
  };
  const use = async () => {
    if (!out?.rate) return;
    try { await api.put("/shop-settings/labor-rate", { hourly_rate: out.rate }); toast.success("Valor da hora atualizado"); refresh?.(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <section className={card} data-testid="calc-hour">
      <div className="flex items-center gap-2">
        <Wrench className="h-4 w-4 text-green-neon" />
        <h2 className="font-bold text-zinc-900 dark:text-zinc-100">Calculadora de Mão de Obra</h2>
      </div>

      <Field label="Custo fixo mensal (R$)">
        <Input data-testid="calc-fixed" type="number" value={f.fixed} onChange={set("fixed")} placeholder="Ex.: 15000" className={inputCls} />
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Seg–Sex · início">
          <Input data-testid="calc-wstart" type="time" value={f.wStart} onChange={set("wStart")} className={inputCls} />
        </Field>
        <Field label="Seg–Sex · fim">
          <Input data-testid="calc-wend" type="time" value={f.wEnd} onChange={set("wEnd")} className={inputCls} />
        </Field>
        <Field label="Sábado · início (opcional)">
          <Input data-testid="calc-sstart" type="time" value={f.sStart} onChange={set("sStart")} className={inputCls} />
        </Field>
        <Field label="Sábado · fim (opcional)">
          <Input data-testid="calc-send" type="time" value={f.sEnd} onChange={set("sEnd")} className={inputCls} />
        </Field>
        <Field label="Almoço seg–sex (h)">
          <Input data-testid="calc-wlunch" type="number" min="0" step="0.25" value={f.wLunch} onChange={set("wLunch")} className={inputCls} />
        </Field>
        <Field label="Intervalo sáb. (h)">
          <Input data-testid="calc-slunch" type="number" min="0" step="0.25" value={f.sLunch} onChange={set("sLunch")} className={inputCls} />
        </Field>
      </div>

      <Field label={`Horas trabalhadas / mês · por mecânico`}>
        <Input
          data-testid="calc-hours-override"
          type="number"
          value={f.hoursOverride}
          onChange={set("hoursOverride")}
          placeholder={`${computedHoursPerMech}h calculadas pelo horário`}
          className={inputCls}
        />
        <p data-testid="calc-hours-help" className="mt-1 text-[11px] text-zinc-500">
          {num(f.hoursOverride) > 0
            ? `Usando ${num(f.hoursOverride)}h editadas por você.`
            : `Seg–sex: ${fmtH(diffHours(f.wStart, f.wEnd))}h − ${fmtH(num(f.wLunch))}h almoço = ${fmtH(wDay)}h/dia × 5${sDay > 0 || num(f.sLunch) > 0 ? ` + sábado ${fmtH(sDay)}h` : ""} = ${fmtH(perWeek)}h/semana × 52 ÷ 12 ≈ ${computedHoursPerMech}h/mês. Edite se quiser.`}
        </p>
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Qtd. de mecânicos">
          <Input data-testid="calc-mechs" type="number" value={f.mechs} onChange={set("mechs")} className={inputCls} />
        </Field>
        <Field label="Margem desejada (%)">
          <Input data-testid="calc-margin" type="number" value={f.margin} onChange={set("margin")} className={inputCls} />
        </Field>
      </div>

      <Field label="Faixa de valor do veículo">
        <select data-testid="calc-tier" value={f.tier} onChange={set("tier")} className={selectCls}>
          {Object.entries(TIER).map(([k, v]) => (
            <option key={k} value={k}>{v.lbl} ({v.help})</option>
          ))}
        </select>
      </Field>

      {fixedHint && num(f.fixed) === 0 && (
        <div
          data-testid="calc-fixed-hint"
          className="flex items-start gap-2 rounded-md border p-3 text-xs
                     border-amber-300 bg-amber-50 text-amber-900
                     dark:border-amber-700/60 dark:bg-amber-900/30 dark:text-amber-100"
        >
          <Lightbulb className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-300" />
          <span>Lance suas contas fixas em <Link to="/owner/financeiro" className="underline font-semibold">Financeiro</Link> ou digite o total aqui para o cálculo ficar realista.</span>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 pt-1">
        <Button data-testid="calc-run" onClick={run} className="w-full bg-green-brand hover:bg-green-brand/90 text-white font-bold h-11">Calcular</Button>
        <Button data-testid="calc-clear" type="button" variant="outline" onClick={clear} className="w-full h-11">Limpar</Button>
      </div>

      {out && (
        <div data-testid="calc-result" className="rounded-xl border border-green-neon/40 bg-green-brand/5 p-4 space-y-2">
          <div className="text-xs font-mono-caps text-zinc-500 dark:text-zinc-400">Resultado</div>
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <div className="text-[11px] text-zinc-500 dark:text-zinc-400">Custo da hora</div>
              <div className="font-bold text-zinc-900 dark:text-zinc-100 text-lg">{brl(out.cost)}/h</div>
            </div>
            <div>
              <div className="text-[11px] text-zinc-500 dark:text-zinc-400">Valor sugerido</div>
              <div data-testid="calc-rate" className="font-display text-2xl font-black text-green-neon">{brl(out.rate)}/h</div>
            </div>
            <div className="text-[11px] text-zinc-500 dark:text-zinc-400">
              {out.hoursMonth}h/mês{user?.shop?.hourly_rate ? ` · atual ${brl(user.shop.hourly_rate)}/h` : ""}
            </div>
          </div>
          <Button data-testid="calc-use-rate" disabled={!out.rate || user?.shop_id === "all"} onClick={use} className="w-full bg-green-brand text-white font-bold">
            Usar como valor da hora da oficina
          </Button>
        </div>
      )}
    </section>
  );
}

const PART_K = { nova_oem: 1.6, nova_paralela: 1.9, revisada: 2.1, usada: 2.4 };
const PART_LBL = [
  ["nova_oem", "Nova original (OEM)"],
  ["nova_paralela", "Nova paralela"],
  ["revisada", "Revisada"],
  ["usada", "Usada"],
];

function PartsCard() {
  const { user, refresh } = useAuth();
  const [rows, setRows] = useState(() =>
    user?.shop?.part_origins?.length
      ? user.shop.part_origins
      : [{ name: "Autopeças", markup: 60 }, { name: "Concessionária", markup: 35 }, { name: "Distribuidor", markup: 80 }]
  );
  const [sim, setSim] = useState({ cost: "", origin: "", kind: "", markup: "" });
  const [out, setOut] = useState(null);
  const setRow = (i, k, v) => setRows((p) => p.map((r, j) => (j === i ? { ...r, [k]: v } : r)));

  const save = async () => {
    try { await api.put("/shop-settings/part-origins", { items: rows.map((r) => ({ name: r.name, markup: num(r.markup) })) }); toast.success("Origens salvas"); refresh?.(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const run = () => {
    if (!num(sim.cost) || sim.origin === "" || !sim.kind) {
      toast.error("Preencha custo, origem e tipo de peça.");
      setOut(null); return;
    }
    const originMk = num(rows[Number(sim.origin)]?.markup);
    const typeK = PART_K[sim.kind];
    const mk = num(sim.markup) > 0 ? num(sim.markup) : originMk;
    const price = Math.round(num(sim.cost) * typeK * (1 + mk / 100) * 100) / 100;
    const profit = price - num(sim.cost);
    const margin = price ? (profit / price) * 100 : 0;
    setOut({ price, profit, margin });
  };
  const clear = () => { setSim({ cost: "", origin: "", kind: "", markup: "" }); setOut(null); };

  return (
    <section className={card} data-testid="calc-origin">
      <div className="flex items-center gap-2">
        <Package className="h-4 w-4 text-sky-400" />
        <h2 className="font-bold text-zinc-900 dark:text-zinc-100">Calculadora de Peças</h2>
      </div>

      <Field label="Preço de custo (R$)">
        <Input
          data-testid="sim-cost"
          type="number"
          value={sim.cost}
          onChange={(e) => setSim((p) => ({ ...p, cost: e.target.value }))}
          placeholder="Digite o valor pago"
          className={inputCls}
        />
      </Field>

      <Field label="Onde comprou">
        <select
          data-testid="sim-origin"
          value={sim.origin}
          onChange={(e) => setSim((p) => ({ ...p, origin: e.target.value }))}
          className={selectCls}
        >
          <option value="">Selecione a origem...</option>
          {rows.map((r, i) => <option key={i} value={i}>{r.name || `Origem ${i + 1}`}</option>)}
        </select>
      </Field>

      <Field label="O que é">
        <select
          data-testid="sim-kind"
          value={sim.kind}
          onChange={(e) => setSim((p) => ({ ...p, kind: e.target.value }))}
          className={selectCls}
        >
          <option value="">Selecione o tipo de peça...</option>
          {PART_LBL.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
      </Field>

      <Field label="Markup manual (%) · opcional">
        <Input data-testid="sim-markup" type="number" value={sim.markup} onChange={(e) => setSim((p) => ({ ...p, markup: e.target.value }))} placeholder="deixe vazio p/ usar a origem" className={inputCls} />
      </Field>

      <div className="grid grid-cols-2 gap-3 pt-1">
        <Button data-testid="sim-run" onClick={run} className="w-full bg-green-brand hover:bg-green-brand/90 text-white font-bold h-11">Calcular</Button>
        <Button data-testid="sim-clear" type="button" variant="outline" onClick={clear} className="w-full h-11">Limpar</Button>
      </div>

      {out && (
        <div data-testid="sim-result" className="rounded-xl border border-sky-400/40 bg-sky-500/5 p-4 space-y-2">
          <div className="text-xs font-mono-caps text-zinc-500 dark:text-zinc-400">Resultado</div>
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <div className="text-[11px] text-zinc-500 dark:text-zinc-400">Preço de venda</div>
              <div data-testid="sim-price" className="font-display text-2xl font-black text-sky-500 dark:text-sky-200">{brl(out.price)}</div>
            </div>
            <div>
              <div className="text-[11px] text-zinc-500 dark:text-zinc-400">Lucro</div>
              <div className="font-bold text-green-neon">{brl(out.profit)}</div>
            </div>
            <div>
              <div className="text-[11px] text-zinc-500 dark:text-zinc-400">Margem</div>
              <div data-testid="sim-profit" className="font-bold text-green-neon">{out.margin.toFixed(1)}%</div>
            </div>
          </div>
        </div>
      )}

      <div className="pt-2 border-t border-zinc-200 dark:border-zinc-800">
        <div className="text-xs font-mono-caps text-zinc-500 dark:text-zinc-400 mb-2">Markup por origem (salvo na oficina)</div>
        <div className="space-y-2">
          {rows.map((r, i) => (
            <div key={i} className="flex gap-2">
              <Input data-testid={`origin-name-${i}`} value={r.name} onChange={(e) => setRow(i, "name", e.target.value)} className={inputCls} />
              <Input data-testid={`origin-markup-${i}`} type="number" value={r.markup} onChange={(e) => setRow(i, "markup", e.target.value)} className={`${inputCls} w-24`} />
              <span className="self-center text-zinc-500 text-sm">%</span>
              <button type="button" onClick={() => setRows((p) => p.filter((_, j) => j !== i))} className="text-zinc-500 hover:text-red-400" aria-label="Remover"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
          <div className="flex gap-2">
            <Button type="button" variant="outline" data-testid="origin-add" onClick={() => setRows((p) => [...p, { name: "", markup: 50 }])} className="bg-zinc-50 dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800"><Plus className="h-4 w-4 mr-1" /> Origem</Button>
            <Button type="button" data-testid="origin-save" disabled={user?.shop_id === "all"} onClick={save} className="bg-green-brand text-white font-bold">Salvar origens</Button>
          </div>
          <p className="text-[11px] text-zinc-500">Ao cadastrar uma peça no Estoque, escolha a origem e o preço sugerido é calculado pelo custo.</p>
        </div>
      </div>
    </section>
  );
}

export default function OwnerCalculators() {
  return (
    <div className="space-y-5 animate-fade-up" data-testid="calculators-page">
      <PageHead icon={Calculator} kicker="Financeiro" title="Calculadoras" />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 items-start">
        <LaborCard />
        <PartsCard />
      </div>
    </div>
  );
}
